import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SERVICES = [
  {
    n: "01",
    title: "Direct & Indirect Taxation",
    body: "Income tax planning, returns and notices for individuals, professionals and businesses. GST advisory, registrations, returns and reconciliations.",
  },
  {
    n: "02",
    title: "Statutory & Bank Audits",
    body: "Statutory audits under the Companies Act and rigorous bank audits — delivered with disciplined documentation and clarity for every stakeholder.",
  },
  {
    n: "03",
    title: "Internal Audit & Risk Advisory",
    body: "Process reviews, risk-based internal audits and SOP design that surface insights — not just findings.",
  },
  {
    n: "04",
    title: "Due Diligence",
    body: "Financial, tax and operational due diligence for transactions, fundraises and acquisitions. Founder-friendly, partner-led.",
  },
  {
    n: "05",
    title: "Accounting & Bookkeeping",
    body: "Outsourced accounting, monthly closes, MIS and dashboards for founders who want clean books without building a finance team.",
  },
  {
    n: "06",
    title: "Startup & Business Advisory",
    body: "Entity setup, capital structuring, founder agreements, and growth-stage finance support — informed by a decade of running a business of my own.",
  },
];

const KNOWLEDGE_VIDEOS = [
  { id: "g8kldzIfO-k", tag: "Knowledge · Tax", title: "How to save tax — practical insights for Indian businesses" },
  { id: "jN5tGJiu6WE", tag: "Knowledge · Finance", title: "Smart financial moves every entrepreneur should make" },
];

const FAQ = [
  {
    q: "Who do you typically work with?",
    a: "Founders, professionals, family businesses and growing brands across India. Anyone who wants their financial decisions led by clarity, not by paperwork.",
  },
  {
    q: "Do I need to be in Delhi-NCR to work with you?",
    a: "No. Most of our work is delivered remotely with periodic on-ground audits where required. Clients across India and a few overseas.",
  },
  {
    q: "How does engagement begin?",
    a: "Start with a 30–45 minute discovery call. We understand your situation, scope the work, and share a written proposal with timelines and pricing.",
  },
  {
    q: "Are engagements customised?",
    a: "Always. No two businesses are the same — every plan is tailored to goals, risk appetite and team capacity.",
  },
];

export default function Services() {
  return (
    <>
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 md:pt-24 pb-16 md:pb-24">
        <p className="overline fade-up"><span className="section-mark">Services</span></p>
        <h1 className="font-display mt-7 text-[44px] sm:text-6xl md:text-7xl font-light tracking-[-0.03em] leading-[0.98] text-ink fade-up fade-up-2">
          Financial advisory<br />without the <span className="font-display-italic text-terracotta">opacity</span>.
        </h1>
        <p className="mt-8 max-w-2xl text-[15px] sm:text-lg text-ink-soft leading-[1.7] fade-up fade-up-3">
          Six core practice areas. Partner-led delivery. Built for founders, professionals
          and businesses that take their books — and their growth — seriously.
        </p>
      </section>

      <section className="border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 md:py-28">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
            {SERVICES.map((s, i) => (
              <li
                key={s.n}
                data-testid={`service-${i}`}
                className="group border-b border-[hsl(var(--line))] py-10 md:py-12"
              >
                <div className="flex items-start gap-6">
                  <span className="font-display tabular text-sm md:text-base font-light text-stone-400 mt-2 w-8 shrink-0">{s.n}</span>
                  <div className="flex-1">
                    <h3 className="font-display text-2xl md:text-3xl font-light tracking-[-0.02em] text-ink group-hover:text-terracotta transition-colors">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-ink-soft text-[15px] md:text-base max-w-xl leading-[1.75]">{s.body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* WATCH & LEARN — CA KNOWLEDGE VIDEOS */}
      <section className="bg-white border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
            <div>
              <p className="overline"><span className="section-mark">Watch & learn</span></p>
              <h2 className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.03em] text-ink leading-[1.05]">
                CA knowledge,<br /><span className="font-display-italic">on tap</span>.
              </h2>
              <p className="mt-6 text-ink-soft text-[15px] md:text-lg max-w-xl leading-[1.7]">
                Free, practical lessons drawn from a decade of practice. A glimpse of the
                clarity you'll get on a discovery call.
              </p>
            </div>
            <a
              href="https://www.youtube.com/@ca_nitinsharma"
              target="_blank"
              rel="noreferrer"
              data-testid="services-yt-link"
              className="inline-flex items-center text-[13px] tracking-wide font-medium text-ink hover:text-terracotta"
            >
              Subscribe on YouTube <ArrowUpRight className="ml-1" size={14} />
            </a>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {KNOWLEDGE_VIDEOS.map((v) => (
              <a
                key={v.id}
                data-testid={`services-video-${v.id}`}
                href={`https://www.youtube.com/watch?v=${v.id}`}
                target="_blank"
                rel="noreferrer"
                className="group block card-lift"
              >
                <div className="relative aspect-video overflow-hidden rounded-2xl bg-stone-100 ring-1 ring-stone-200">
                  <img
                    src={`https://img.youtube.com/vi/${v.id}/maxresdefault.jpg`}
                    alt={v.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    onError={(e) => {
                      if (!e.currentTarget.dataset.fallback) {
                        e.currentTarget.dataset.fallback = "1";
                        e.currentTarget.src = `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`;
                      }
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded-full bg-white/95 backdrop-blur w-16 h-16 grid place-items-center shadow-[0_8px_30px_-8px_rgba(0,0,0,0.35)]">
                      <span className="ml-1 w-0 h-0 border-y-[9px] border-y-transparent border-l-[14px] border-l-ink" />
                    </span>
                  </div>
                </div>
                <p className="mt-5 text-[11px] tracking-[0.18em] uppercase font-semibold text-stone-500">{v.tag}</p>
                <p className="font-display mt-2 text-xl md:text-2xl font-light tracking-tight text-ink group-hover:text-terracotta transition-colors leading-snug">
                  {v.title}
                </p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="overline">Frequently asked</p>
            <h2 className="font-display mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight text-ink leading-[1.05]">
              Clear answers,<br />confident decisions.
            </h2>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="services-faq">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-[hsl(var(--line))]">
                  <AccordionTrigger
                    data-testid={`faq-q-${i}`}
                    className="text-left font-display text-lg md:text-xl font-bold text-ink hover:no-underline py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-ink-soft text-base md:text-lg leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[hsl(var(--line))]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="rounded-3xl bg-ink text-white p-10 md:p-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
            <div className="max-w-2xl">
              <p className="overline text-white/70">Get started</p>
              <h2 className="font-display mt-5 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.02]">
                Book a free 30–45 minute<br />discovery call.
              </h2>
              <p className="mt-5 text-white/80 max-w-lg">
                Tell us where you are, where you want to go, and where the books are getting
                in the way. We’ll come back with a clear plan.
              </p>
            </div>
            <Link
              to="/contact"
              data-testid="services-cta"
              className="inline-flex items-center rounded-full bg-terracotta hover:bg-terracotta-deep px-7 py-4 font-semibold transition-colors self-start"
            >
              Book a Discovery Call <ArrowUpRight className="ml-2" size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
